/**
 * Machine Setup API
 * API client for all machine-setup endpoints
 * Base URL: /api/v1/machine-setup
 */

import { BASE_AUTH_OFFICE_API } from '@/core/api-config';
import { TokenManager } from '@/core/auth/token-manager';
import { ApiErrorMiddleware } from '@/core/api-error-middleware';
import { useOperatorStore } from '@/core/stores/operator-store';
import type {
  MachineTypesResponse,
  MachineGroupsResponse,
  SerialValidationResponse,
  CreateMachineRequest,
  CreateMachineResponse,
  ValidateMachineConfigResponse,
  MachineTypeSettingsResponse,
  SaveTypeSettingsRequest,
  AddCustomMachineTypeRequest,
  CreateThirdPartyMachineRequest,
  ThirdPartyManufacturer,
  DeviceSuggestion,
  LynxMachineInfoResponse,
  ApplyAttributeItem,
  BulkCreateMachineRequest,
  BulkCreateMachineResponse,
} from '../types/machine-setup-types';

export class MachineSetupApi {
  constructor(
    private readonly baseUrl: string = BASE_AUTH_OFFICE_API,
    private readonly tokenManager: TokenManager
  ) {}

  private async getAuthHeadersWithOperator(): Promise<Record<string, string>> {
    const selectedOperator = useOperatorStore.getState().selectedOperator;

    if (!selectedOperator) {
      console.error('MachineSetupApi: No operator selected, preventing API call');
      throw new Error('No operator selected. Please select an operator first.');
    }

    const headers = await this.tokenManager.getAuthHeaders();
    const operatorId = selectedOperator.operatorId.toString();

    const result: Record<string, string> = {
      'Content-Type': 'application/json',
      ...headers,
      'X-Operator-Id': operatorId,
    };

    if (selectedOperator.appId && selectedOperator.appId !== 0) {
      result['X-APP-ID'] = selectedOperator.appId.toString();
    }

    return result;
  }

  /**
   * Returns all supported machine types for the creation wizard.
   */
  async getMachineTypes(): Promise<MachineTypesResponse> {
    const headers = await this.getAuthHeadersWithOperator();

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}/machine-setup/types`,
      { method: 'GET', headers },
      'MachineSetupApi',
      '/machine-setup/types'
    );

    return response.json();
  }

  /**
   * Validates a device serial number: checks existence and assignment status.
   * A device is usable when deviceFound=true and isAssigned=false.
   */
  async validateSerial(serialNumber: string): Promise<SerialValidationResponse> {
    const headers = await this.getAuthHeadersWithOperator();

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}/machine-setup/validate-serial`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({ serialNumber }),
      },
      'MachineSetupApi',
      '/machine-setup/validate-serial'
    );

    return response.json();
  }

  /**
   * Creates a new machine in Lynx with preset attributes for the selected type.
   */
  async createMachine(request: CreateMachineRequest): Promise<CreateMachineResponse> {
    const headers = await this.getAuthHeadersWithOperator();

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}/machine-setup/create`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify(request),
      },
      'MachineSetupApi',
      '/machine-setup/create'
    );

    return response.json();
  }

  /**
   * Returns all machine groups for a given actor from Lynx.
   * Called during the Configuration step to populate the Machine Group dropdown.
   */
  async getMachineGroups(actorId: number): Promise<MachineGroupsResponse> {
    const headers = await this.getAuthHeadersWithOperator();

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}/machine-setup/actors/${actorId}/machine-groups`,
      { method: 'GET', headers },
      'MachineSetupApi',
      `/machine-setup/actors/${actorId}/machine-groups`
    );

    return response.json();
  }

  /**
   * Returns active direct child locations of the given operator actor.
   * Inactive locations (ActorStatus != 1 in Lynx) are filtered out server-side.
   */
  async getActorsForOperator(operatorId: number): Promise<Array<{ actorId: number; name: string; parentActorId?: number }>> {
    const headers = await this.getAuthHeadersWithOperator();

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}/machine-setup/actors/${operatorId}/active-locations`,
      { method: 'GET', headers },
      'MachineSetupApi',
      `/machine-setup/actors/${operatorId}/active-locations`
    );

    const data = await response.json();
    if (data.isSuccess === false) {
      throw new Error(data.errorMessage ?? 'Failed to load locations');
    }
    return (data.locations ?? []).map((loc: Record<string, unknown>) => ({
      actorId: loc.actorId as number,
      name: (loc.name as string) || `Actor ${loc.actorId}`,
      parentActorId: loc.parentActorId as number | undefined,
    }));
  }

  /**
   * Validates an existing machine's live attribute config against its type preset.
   * Note: the back-office endpoint is [HttpPost] by design — machineTypeKey is passed
   * as a query string parameter (no request body). This is intentional to allow the
   * back-office to perform side-effect-free validation without a GET payload constraint.
   */
  async validateMachineConfig(
    machineId: number,
    machineTypeKey: string
  ): Promise<ValidateMachineConfigResponse> {
    const headers = await this.getAuthHeadersWithOperator();
    const params = new URLSearchParams({ machineTypeKey });

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}/machine-setup/validate/${machineId}?${params.toString()}`,
      { method: 'POST', headers },
      'MachineSetupApi',
      `/machine-setup/validate/${machineId} typeKey=${machineTypeKey}`
    );

    return response.json();
  }

  /**
   * Returns all machine types with their current static attribute lists. Admin only.
   */
  async getTypeSettings(): Promise<MachineTypeSettingsResponse> {
    const headers = await this.getAuthHeadersWithOperator();

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}/machine-setup/type-settings`,
      { method: 'GET', headers },
      'MachineSetupApi',
      '/machine-setup/type-settings'
    );

    return response.json();
  }

  /**
   * Saves a static attribute override list for one machine type. Admin only.
   */
  async saveTypeSettings(machineTypeKey: string, data: SaveTypeSettingsRequest): Promise<{ isSuccess: boolean; errorMessage?: string }> {
    const headers = await this.getAuthHeadersWithOperator();

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}/machine-setup/type-settings/${machineTypeKey}`,
      { method: 'PUT', headers, body: JSON.stringify(data) },
      'MachineSetupApi',
      `/machine-setup/type-settings/${machineTypeKey}`
    );

    return response.json();
  }

  /**
   * Adds a new custom machine type. Admin only.
   */
  async addCustomMachineType(data: AddCustomMachineTypeRequest): Promise<{ isSuccess: boolean; errorMessage?: string }> {
    const headers = await this.getAuthHeadersWithOperator();

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}/machine-setup/type-settings`,
      { method: 'POST', headers, body: JSON.stringify(data) },
      'MachineSetupApi',
      '/machine-setup/type-settings (POST)'
    );

    return response.json();
  }

  /**
   * Deletes a custom machine type. Admin only.
   */
  async deleteCustomMachineType(machineTypeKey: string): Promise<{ isSuccess: boolean; errorMessage?: string }> {
    const headers = await this.getAuthHeadersWithOperator();

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}/machine-setup/type-settings/${machineTypeKey}`,
      { method: 'DELETE', headers },
      'MachineSetupApi',
      `/machine-setup/type-settings/${machineTypeKey} (DELETE)`
    );

    return response.json();
  }

  /**
   * Returns the active manufacturers list from DCS. Used for third-party charger selection.
   */
  async getManufacturers(): Promise<ThirdPartyManufacturer[]> {
    const headers = await this.getAuthHeadersWithOperator();

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}/dcs/manufacturers`,
      { method: 'GET', headers },
      'MachineSetupApi',
      '/dcs/manufacturers'
    );

    const data = await response.json();
    const items: ThirdPartyManufacturer[] = Array.isArray(data) ? data : (data.rows ?? data.value ?? []);
    return items.filter(m =>
      (m as { status?: number }).status !== 0 &&
      m.manufacturerName.toLowerCase().trim() !== 'ev meter'
    );
  }

  /**
   * Returns devices registered at the given actor for serial number autocomplete.
   */
  async getDevicesForActor(actorId: number): Promise<DeviceSuggestion[]> {
    const headers = await this.getAuthHeadersWithOperator();

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}/machine-setup/actors/${actorId}/devices`,
      { method: 'GET', headers },
      'MachineSetupApi',
      `/machine-setup/actors/${actorId}/devices`
    );

    return response.json();
  }

  /**
   * Returns basic Lynx machine info (name, MachineTypeId, MachineModelId) for a given machine ID.
   * Used in the validation panel to auto-detect hardware type.
   */
  async getMachineInfo(machineId: number): Promise<LynxMachineInfoResponse> {
    const headers = await this.getAuthHeadersWithOperator();

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}/machine-setup/machine-info/${machineId}`,
      { method: 'GET', headers },
      'MachineSetupApi',
      `/machine-setup/machine-info/${machineId}`
    );

    return response.json();
  }

  /**
   * Applies a list of attribute updates directly to an existing machine.
   */
  async applyAttributes(machineId: number, attributes: ApplyAttributeItem[]): Promise<{ isSuccess: boolean; message: string | null }> {
    const headers = await this.getAuthHeadersWithOperator();

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}/machine-setup/${machineId}/apply-attributes`,
      { method: 'POST', headers, body: JSON.stringify({ attributes }) },
      'MachineSetupApi',
      `/machine-setup/${machineId}/apply-attributes`
    );

    return response.json();
  }

  /**
   * Creates multiple machines with the same settings but without a device serial.
   * Names are auto-generated as "{baseName} {n}" starting at 2.
   */
  async createMachineBulk(request: BulkCreateMachineRequest): Promise<BulkCreateMachineResponse> {
    const headers = await this.getAuthHeadersWithOperator();

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}/machine-setup/create-bulk`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify(request),
      },
      'MachineSetupApi',
      '/machine-setup/create-bulk'
    );

    return response.json();
  }

  /**
   * Creates a third-party OCPP charger machine (MachineNumber only, no device, no attributes).
   */
  async createThirdPartyMachine(request: CreateThirdPartyMachineRequest): Promise<CreateMachineResponse> {
    const headers = await this.getAuthHeadersWithOperator();

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}/machine-setup/create-third-party`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify(request),
      },
      'MachineSetupApi',
      '/machine-setup/create-third-party'
    );

    return response.json();
  }
}
